import {
    html,
    LitElement,
    TemplateResult,
    CSSResult,
    css
} from 'lit';
import { customElement, property, state} from 'lit/decorators.js';
import { store } from '../redux/store';
import { loadStyle, EVENTS } from '../api/ui';
import { ObjectManager } from '../objectModelV2/objectManager';
import { getConvertedTemperature } from '../api/graphDataApi';

@customElement('berner-weather')
export class BernerWeather extends LitElement {
    private _initialized = false;

    @state()
    _weather: Weather;

    private _siteId = '';
    @property({ attribute: 'site-id' })
    get siteId(): string {
        return this._siteId;
    }

    set siteId(value: string) {
        this._siteId = value;
        this.getData();
        this.requestUpdate();
    }

    @property({ attribute: 'sites' })
    sites: string;

    private siteName: string;
    private allSites: string[];
    private _objectManager: ObjectManager;

    // class css-loading is removed as soon as the external css is loaded
    public static get styles(): CSSResult {
        return css`
            .css-loading {
                display: none;
            }
        `;
    }

    public connectedCallback(): void {
        window.addEventListener(EVENTS.CHANGE_TEMP_UNIT, this.onTempUnitChanged as any);
        super.connectedCallback();
    }

    public disconnectedCallback(): void {
        window.removeEventListener(EVENTS.CHANGE_TEMP_UNIT, this.onTempUnitChanged as any);
        super.disconnectedCallback();
    }

    onTempUnitChanged = (): void => {
        this.getData();
        this.requestUpdate();
    }

    onSiteSelect = (left: boolean): void => {
        let index = this.allSites.findIndex((site) => {return site === this.siteId});
        if (left) {
            if (index === 0) {
                index = this.allSites.length - 1;
            } else {
                index -= 1;
            }
        } else {
            if (index === this.allSites.length - 1) {
                index = 0;
            } else {
                index += 1;
            }
        }

        this.siteId = this.allSites[index];
        this.siteName = this._objectManager.getSite(this.siteId)?.name;
    }

    firstUpdated = async (): Promise<void> => {
        await loadStyle('./css/berner-weather.css', this.shadowRoot);

        const { objectManager } = store.getState();
        this._objectManager = objectManager;

        this.allSites = this.sites.split(':');
        this.siteName = this._objectManager.getSite(this.siteId)?.name;
        this.getData();
        this._initialized = true;
        this.requestUpdate();
    };

    updated(): void {
        this.siteName = this._objectManager?.getSite(this.siteId)?.name;
    }

    async getData(): Promise<void> {
        if (!this._objectManager) return;
        const site = this._objectManager.getSite(this.siteId);
        this._weather = await site?.getCurrentWeather();
        this._weather.currentTemperature = getConvertedTemperature(this._weather.currentTemperature);
        this._weather.maximumTemperature = getConvertedTemperature(this._weather.maximumTemperature);
        this._weather.maximumTemperature = getConvertedTemperature(this._weather.maximumTemperature);
        this._weather.feelsLikeTemperature = getConvertedTemperature(this._weather.feelsLikeTemperature);
    }

    renderContent(): TemplateResult {
        const displayTemp = this._weather?.currentTemperature ? `${this._weather?.currentTemperature?.toLocaleString("en-US", { maximumFractionDigits: 0, minimumFractionDigits: 0 })}°` : '';
        const displayHumidity = this._weather?.relativeHumidity ? `${this._weather?.relativeHumidity}%` : '';

        return html`
            <div class="card">
                <section class="card-body">
                    <div class="row content-row header">
                        <div class="col text-left">
                            <div class="row header top-row">
                                <div class="icon top-row-icon">
                                    <img
                                        src="./assets/icon_weather.svg"
                                        alt="users"
                                    />
                                </div>
                                <span class="card-title text-white title top-row-title"
                                    >${'Weather' +
                                    (this.siteName
                                        ? ' for ' + this.siteName
                                        : '')}
                                </span>
                                <div class="site-cycle-button-container top-row-buttons">
                                    <button
                                        type="button"
                                        class="btn site-cycle-button"
                                        @click=${() => this.onSiteSelect(true)}>&#9664;
                                    </button>
                                    <button
                                        type="button"
                                        class="btn site-cycle-button rotate"
                                        @click=${() => this.onSiteSelect(false)}>&#9664;
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <article class="row pt-5">
                        <div class="col-6 left-element">
                            <h5 class="mb-3">Outside</h5>
                            <p class="temperature ml-2">
                                ${displayTemp}
                            </p>
                        </div>
                        <div class="col-6">
                            <h5 class="mb-3">Humidity</h5>
                            <p class="humidity ml-2">
                                ${displayHumidity}
                            </p>
                        </div>
                    </article>
                </section>
            </div>
        `;
    }

    render(): TemplateResult {
        return html`
        <link id="css-target">
        <div class="css-loading" id="root">
            <div class="col-lg-12 mx-auto no-gutters h-100 p-0">
                ${this._initialized ? html`${this.renderContent()}` : html``}
            </div>
        </div>`;
    }
}
